import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Typography, 
  Box,
  Alert,
  AlertTitle,
  CircularProgress,
  Link
} from '@mui/material';
import { useAppSelector, useAppDispatch } from 'src/store';
import { httpClient } from 'src/libs';
import { useSnackbar } from 'notistack';
import ChannelPicker from '../Channels/ChannelPicker';
import { getContact, selectContactById } from 'src/features/account/contacts.slice';

interface ContactCallDialogProps {
  open: boolean;
  contactId: string;
  onClose: () => void;
}

const ContactCallDialog: React.FC<ContactCallDialogProps> = ({ 
  open, 
  contactId, 
  onClose 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  
  // Get contact from store using selector
  const contact = useAppSelector(state => selectContactById(state, contactId));
  const contactLoading = useAppSelector(state => 
    state.contacts.loadingContactsIds.includes(contactId)
  );

  // State for form fields
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [promptOverride, setPromptOverride] = useState('');
  
  // State for call status
  const [callStatus, setCallStatus] = useState('idle'); // idle, calling, success, error
  const [callSid, setCallSid] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch contact if not in store
  useEffect(() => {
    if (open && contactId && !contact && !contactLoading) {
      dispatch(getContact(contactId));
    }
  }, [open, contactId, contact, contactLoading, dispatch]);

  // Reset form state when dialog opens
  useEffect(() => {
    if (open) {
      setCallStatus('idle');
      setCallSid('');
      setConversationId('');
      setErrorMessage('');
    }
  }, [open]);

  // Check if contact has a phone number
  const hasPhoneNumber = contact?.phone_number;

  // Handle initiating the call
  const handleInitiateCall = async () => {
    if (!selectedChannel || !contactId) {
      enqueueSnackbar('Please select a channel', { variant: 'error' });
      return;
    }

    setCallStatus('calling');
    
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/contacts/${contactId}/call/outbound`;
      
      const payload = {
        channel_id: selectedChannel.id,
        prompt_override: promptOverride || undefined
      };
      
      const response = await httpClient.post(url, payload);
      
      if (response.data.success) {
        setCallStatus('success');
        setCallSid(response.data.call_sid);
        setConversationId(response.data.conversation_id);
        enqueueSnackbar('Call initiated successfully', { variant: 'success' });
      } else {
        setCallStatus('error');
        setErrorMessage(response.data.error?.message || 'Unknown error occurred');
        enqueueSnackbar('Failed to initiate call', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      setCallStatus('error');
      setErrorMessage(error.response?.data?.error?.message || error.message || 'Unknown error occurred');
      enqueueSnackbar('Failed to initiate call', { variant: 'error' });
    }
  };

  const handleClose = () => {
    // If call is in progress, confirm before closing
    if (callStatus === 'calling' && !window.confirm('A call is currently being initiated. Are you sure you want to close this dialog?')) {
      return;
    }
    onClose();
  };

  const renderCallButton = () => {
    switch (callStatus) {
      case 'idle':
        return (
          <Button 
            onClick={handleInitiateCall} 
            disabled={!selectedChannel || !hasPhoneNumber}
            variant="contained" 
            color="primary"
          >
            Call Contact
          </Button>
        );
      case 'calling':
        return (
          <Button disabled variant="contained">
            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
            Initiating Call...
          </Button>
        );
      case 'success':
        return (
          <Button 
            component={Link} 
            href={`/conversations?conversation_id=${conversationId}`}
            variant="contained" 
            color="success"
            onClick={onClose}
          >
            View Conversation
          </Button>
        );
      case 'error':
        return (
          <Button 
            onClick={handleInitiateCall} 
            variant="contained" 
            color="error"
          >
            Retry Call
          </Button>
        );
      default:
        return null;
    }
  };

  // Render loading state while fetching contact
  if (contactLoading) {
    return (
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Loading Contact...</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Call Contact: {contact?.name || 'Loading...'}</DialogTitle>
      
      <DialogContent>
        {!hasPhoneNumber && !contactLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Missing Phone Number</AlertTitle>
            This contact does not have a phone number. Please add a phone number to the contact first.
          </Alert>
        )}

        {callStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Error Initiating Call</AlertTitle>
            {errorMessage}
          </Alert>
        )}

        {callStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Call Initiated Successfully</AlertTitle>
            Call SID: {callSid}
            <br />
            A new conversation has been created. You can track the call there.
          </Alert>
        )}

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Select Channel
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which Oppy channel will make the call. The channel must have a phone number.
        </Typography>
        <ChannelPicker
          selectedChannel={selectedChannel}
          setSelectedChannel={setSelectedChannel}
          inputType="phone"
        />

        {/* add initial value to this textfield */}
        <TextField
          margin="dense"
          label={`Add flavor, mandatory greeting, or instructions for ${selectedChannel?.chatbot_name || "your Oppy"}`}
          helperText={`Add flavor, mandatory greeting, or instructions for ${selectedChannel?.chatbot_name || "your Oppy"}`}
          type="text"
          defaultValue={`IMPORTANT: You must greet the contact by their first name and ask if you're catching them right in the middle of something before you move on.`}
          fullWidth
          multiline
          rows={3}
          value={promptOverride}
          onChange={(e) => setPromptOverride(e.target.value)}
          sx={{ mt: 2 }}
        />

        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            How this works:
          </Typography>
          <Typography variant="body2" component="ol" sx={{ pl: 2 }}>
            <li>
              {selectedChannel?.chatbot_name || "The selected Oppy"} will call {contact?.name || "the contact"} at {contact?.phone_number || "their phone number"}
            </li>
            <li>
              The call will appear to come from {selectedChannel?.chatbot_name || "the Oppy"} at {selectedChannel?.twilio_phone_number || "the Oppy's phone number"}
            </li>
            <li>
              The AI agent will speak with the contact based on their profile and history
            </li>
            <li>
              The call will be recorded and transcribed automatically
            </li>
            <li>
              You'll be able to review the conversation after it's complete
            </li>
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={callStatus === 'calling'}
        >
          Cancel
        </Button>
        {renderCallButton()}
      </DialogActions>
    </Dialog>
  );
};

export default ContactCallDialog; 
